import React, {
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Center,
  HStack,
  ListItem,
  OrderedList,
  StackProps,
} from '@chakra-ui/react';
import { map } from 'lodash';

import {
  CustomTooltip,
  getIcon,
  getProviderName,
  PlusCountTag,
} from 'components/DataDisplay';
import { formatTooltip } from 'components/DataDisplay/Tooltip/utils';
import { getVisibleCloudIconCount } from 'containers/Dashboard/utils/utils';

// `justify` and `iconSize` are optional so a caller can center the icons or
// resize them. Default icon size is 5; dashboard summary cards pass 4.
export interface CloudIconsProps {
  providers?: string[];
  justify?: StackProps['justify'];
  iconSize?: number | string;
}

// Renders cloud provider icons on a single line and collapses the ones that
// do not fit into a +N tag whose tooltip lists the hidden providers.
export const CloudIcons: FC<CloudIconsProps> = ({
  providers = [],
  justify = 'flex-end',
  iconSize = 5,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iconsRef = useRef<HTMLDivElement>(null);
  const plusRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(providers.length);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const icons = iconsRef.current;
    if (!container || !icons) return;

    const iconWidths = Array.from(icons.children).map(
      el => (el as HTMLElement).offsetWidth,
    );
    const gap = parseFloat(window.getComputedStyle(icons).columnGap) || 0;
    setVisibleCount(
      getVisibleCloudIconCount({
        containerWidth: container.clientWidth,
        iconWidths,
        gap,
        plusWidth: plusRef.current?.offsetWidth ?? 0,
      }),
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined' || !containerRef.current) {
      return undefined;
    }
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [measure, providers]);

  const renderIcon = (o: string, index: number) => (
    <CustomTooltip
      label={getProviderName(o)}
      key={index + 'cloud-provider-tooltip'}
    >
      <Center boxSize={iconSize}>
        <Center>{getIcon(o)}</Center>
      </Center>
    </CustomTooltip>
  );

  const remaining = providers.slice(visibleCount);

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="full"
      minW={0}
      overflow="hidden"
    >
      <HStack justify={justify}>
        {map(providers.slice(0, visibleCount), renderIcon)}
        {remaining.length > 0 && (
          <PlusCountTag
            count={remaining.length}
            flexShrink={0}
            tooltip={formatTooltip({
              header: 'Cloud Providers',
              content: (
                <OrderedList>
                  {map(remaining, o => (
                    <ListItem key={o}>{getProviderName(o)}</ListItem>
                  ))}
                </OrderedList>
              ),
              width: 48,
            })}
          />
        )}
      </HStack>
      {/* Hidden copy of every icon and the widest +N tag, used for measuring */}
      <Box
        position="absolute"
        top={0}
        left={0}
        visibility="hidden"
        pointerEvents="none"
        aria-hidden
      >
        <HStack ref={iconsRef}>{map(providers, renderIcon)}</HStack>
        <Box ref={plusRef} display="inline-block">
          <PlusCountTag count={providers.length} />
        </Box>
      </Box>
    </Box>
  );
};
